import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Button, Modal } from '../../';
import { Form, Alert, Tooltip } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { useNotificationContext } from '../../../providers';
import {
    getDefaultUrl,
    requiresConnectionTest,
    isAzure,
    isAWS,
    isGCP,
    CLOUD_TYPE,
    isAzureLocal,
} from '../../../constants';
import { getCloudComponent } from './cloudAccountTypes';
import { useCloudAccountForm } from './hooks/useCloudAccountForm';
import { useTestConnection } from './hooks/useTestConnection';
import { useTemplateDownload } from './hooks/useTemplateDownload';
import { useVSphereCredentials } from './hooks/useVSphereCredentials';
import { useTokenGeneration } from './hooks/useTokenGeneration';
import {
    useLazyGetCloudAccountByIdQuery,
    useLazyGetAzureSPNDataQuery,
    useCreateCloudAccountMutation,
    useUpdateCloudAccountMutation,
    useCreateOrEnsureTagsMutation,
} from '../../../api';
import {
    getExternalId,
    buildCloudAccountPayload,
    copyToClipboard,
    isDownloadableCloudType,
} from './utils/cloudAccountHelpers';
import './CloudAccountModal.scss';
import { getTokenLabel } from './utils/applianceDownloadHelpers';
import { CloudTypeSelect } from './cloudAccountFields/FormFields/CloudTypeSelect';

const CloudAccountModal = ({ open, onClose, onSuccess, cloudAccountId = null, userOrg }) => {
    const { notifySuccess, notifyError } = useNotificationContext();
    const [selectedCloudType, setSelectedCloudType] = useState(null);
    const [isEditMode, setIsEditMode] = useState(!!cloudAccountId);
    const [tags, setTags] = useState([]);
    const [newTags, setNewTags] = useState([]); // Track new tags that need to be created
    const [setupAppliance, setSetupAppliance] = useState(true);
    const [externalId, setExternalId] = useState('');

    const [triggerGetCloudAccount, { data: cloudAccountData, isLoading: loadingCloudAccount, reset }] =
        useLazyGetCloudAccountByIdQuery();
    const [fetchAzureSPNData, { data: azureSPNData }] = useLazyGetAzureSPNDataQuery();
    const [createCloudAccount, { isLoading: creating }] = useCreateCloudAccountMutation();
    const [updateCloudAccount, { isLoading: updating }] = useUpdateCloudAccountMutation();
    const [createOrEnsureTags] = useCreateOrEnsureTagsMutation();

    const form = useCloudAccountForm(selectedCloudType, isEditMode);
    const testConnection = useTestConnection();
    const templateDownload = useTemplateDownload(userOrg);
    const vsphereCredentials = useVSphereCredentials();
    const tokenGeneration = useTokenGeneration();
    const tokenLabel = getTokenLabel(selectedCloudType);

    const loading = useMemo(() => {
        return creating || updating || loadingCloudAccount;
    }, [creating, updating, loadingCloudAccount]);

    useEffect(() => {
        setExternalId(getExternalId());
    }, []);

    useEffect(() => {
        if (open && !cloudAccountId && userOrg && (isAzure(selectedCloudType) || isAzureLocal(selectedCloudType))) {
            fetchAzureSPNData(userOrg);
        }
    }, [open, cloudAccountId, userOrg, selectedCloudType, fetchAzureSPNData]);

    useEffect(() => {
        if (open && cloudAccountId) {
            triggerGetCloudAccount(cloudAccountId);
        } else if (!open) {
            reset();
        }
    }, [open, cloudAccountId]);

    useEffect(() => {
        if (cloudAccountData && open) {
            setSelectedCloudType(cloudAccountData.type);
            setTags(cloudAccountData.tags || []);
            form.loadFormData(cloudAccountData);

            // Check if can edit vSphere credentials
            if (
                cloudAccountData.appliance?.vm_level_migration_enabled &&
                [CLOUD_TYPE.AWS, CLOUD_TYPE.AZURE, CLOUD_TYPE.AZURE_LOCAL, CLOUD_TYPE.GCP].includes(
                    cloudAccountData.type,
                )
            ) {
                vsphereCredentials.enableEdit();
            }
        }
    }, [cloudAccountData, open]);

    useEffect(() => {
        if (open) {
            if (cloudAccountId) {
                setIsEditMode(true);
            } else {
                resetModal();
            }
        }
    }, [open, cloudAccountId]);

    const resetModal = useCallback(() => {
        setIsEditMode(false);
        setSelectedCloudType(null);
        setTags([]);
        setNewTags([]);
        setSetupAppliance(true);
        form.resetForm();
        testConnection.resetTestConnection();
        vsphereCredentials.reset();
        tokenGeneration.resetTokenGeneration();
    }, [form, testConnection, vsphereCredentials, tokenGeneration]);

    const handleCloudTypeChange = useCallback(
        (cloudType) => {
            setSelectedCloudType(cloudType);

            // Reset all form fields and validation errors
            form.resetForm();

            // Reset all related state
            testConnection.resetTestConnection();
            vsphereCredentials.reset();
            tokenGeneration.resetTokenGeneration();
            setTags([]);
            setNewTags([]);

            // Apply default URL for the new cloud type
            const defaultUrl = getDefaultUrl(cloudType);
            if (defaultUrl) {
                form.applyDefaultUrl(defaultUrl);
            }
        },
        [form, testConnection, vsphereCredentials, tokenGeneration],
    );

    /**
     * Validate cloud account form fields
     * @param {Object} options - Validation options
     * @param {boolean} options.validatePassword - Whether to require password (default: true)
     * @returns {Object} Validation errors object
     */
    const validateFormFields = useCallback(
        (options = {}) => {
            const { validatePassword = true } = options;
            const errors = {};

            // Name is always required
            if (!form.formData?.name?.trim()) {
                errors.name = 'Name is required';
            }

            // Username validation
            if (!form.formData?.username?.trim()) {
                if (isAWS(selectedCloudType)) {
                    errors.username = 'Role is required';
                } else if (isAzure(selectedCloudType) || isAzureLocal(selectedCloudType)) {
                    errors.username = 'Application ID is required';
                } else if (isGCP(selectedCloudType)) {
                    errors.username = 'Google Service Account is required';
                } else {
                    errors.username = 'Username is required';
                }
            }

            // Password validation
            if (validatePassword && !form.formData?.password?.trim()) {
                if (isAzure(selectedCloudType) || isAzureLocal(selectedCloudType)) {
                    errors.password = 'Key Value is required';
                }
            }

            // Tenant ID validation for Azure
            if ((isAzure(selectedCloudType) || isAzureLocal(selectedCloudType)) && !form.formData?.tenant_id?.trim()) {
                errors.tenant_id = 'Directory ID is required';
            }

            return errors;
        },
        [form.formData, selectedCloudType],
    );

    const handleTestConnection = useCallback(async () => {
        const errors = validateFormFields({
            validatePassword: isAzure(selectedCloudType) || isAzureLocal(selectedCloudType),
        });

        if (Object.keys(errors).length > 0) {
            form.setErrors(errors);
            notifyError({ description: 'Please fill in all required fields' });
            return;
        }

        const result = await testConnection.testConnection(selectedCloudType, form.formData, userOrg);

        if (result.success) {
            form.setErrors({ testing: null });
            notifySuccess({ description: 'Connection test successful!' });
        } else {
            notifyError({ description: result.error });
        }
    }, [form, selectedCloudType, userOrg, testConnection, notifyError, notifySuccess, validateFormFields]);

    const handleDownloadTemplate = useCallback(
        async (cloudType, templateOverride = null) => {
            const result = await templateDownload.downloadTemplate(cloudType, templateOverride);

            if (result.success) {
                notifySuccess({ description: 'Template downloaded successfully' });
            } else {
                notifyError({ description: result.error });
            }
        },
        [templateDownload, notifySuccess, notifyError],
    );

    const handleCopyExternalId = useCallback(async () => {
        const result = await copyToClipboard(externalId);
        if (result.success) {
            notifySuccess({ description: 'External ID copied to clipboard' });
        } else {
            notifyError({ description: 'Failed to copy External ID' });
        }
    }, [externalId, notifySuccess, notifyError]);

    const handleReuseExistingSPN = useCallback(() => {
        if (azureSPNData) {
            const tenantId = azureSPNData.last_tenant_id || '';
            const applicationId = azureSPNData.last_application_id || '';
            const keyValue = azureSPNData.last_secret || '';

            // Trigger onChange for each field to ensure proper state update and re-render
            form.handleFieldChange('tenant_id')({ target: { value: tenantId } });
            form.handleFieldChange('username')({ target: { value: applicationId } });
            form.handleFieldChange('password')({ target: { value: keyValue } });

            notifySuccess({ description: 'Azure SPN credentials loaded' });
        }
    }, [azureSPNData, form, notifySuccess]);

    const handleGenerateToken = useCallback(async () => {
        const result = await tokenGeneration.generateApplianceToken(selectedCloudType);

        if (result.success) {
            form.setErrors({ token: null });
            notifySuccess({ description: 'Token generated successfully!' });
        } else {
            const defaultMessage = 'Failed to generate token';
            form.setErrors({ token: result?.error || defaultMessage });
            notifyError({ description: result.error || 'defaultMessage' });
        }
    }, [selectedCloudType, tokenGeneration, notifySuccess, notifyError, form]);

    const handleCopyToken = useCallback(async () => {
        const result = await tokenGeneration.copyTokenToClipboard();

        if (result.success) {
            notifySuccess({ description: 'Token copied to clipboard' });
        } else {
            notifyError({ description: result.error || 'Failed to copy token' });
        }
    }, [tokenGeneration, notifySuccess, notifyError]);

    const handleClose = useCallback(() => {
        resetModal();
        onClose();
    }, [resetModal, onClose]);

    const handleTagsChange = useCallback((allTags, newTagsList = []) => {
        setTags(allTags);
        setNewTags(newTagsList);
    }, []);

    const handleSubmit = useCallback(async () => {
        if (!isEditMode) {
            // For downloadable types, validate token is generated
            if (isDownloadableCloudType(selectedCloudType)) {
                if (!form.formData?.name?.trim()) {
                    form.setErrors({ name: 'Name is required' });
                    notifyError({ description: 'Please fill in all required fields' });
                    return;
                }
                if (!tokenGeneration.actualToken && !tokenGeneration.generatedToken) {
                    const message = `Please generate the ${tokenLabel} before saving`;
                    form.setErrors({ token: message });
                    notifyError({ description: message });
                    return;
                }
                if (!tokenGeneration.applianceId) {
                    notifyError({ description: 'Token generation incomplete. Please regenerate the token.' });
                    return;
                }
            } else {
                // For public cloud types, validate form fields
                const errors = validateFormFields({
                    validatePassword:
                        isAzure(selectedCloudType) || isAzure(selectedCloudType) || isGCP(selectedCloudType),
                });

                if (Object.keys(errors).length > 0) {
                    form.setErrors(errors);
                    notifyError({ description: 'Please fill in all required fields' });
                    return;
                }
            }
        }

        // Check if connection test is required but not done
        if (!isEditMode && requiresConnectionTest(selectedCloudType) && !testConnection.tested) {
            const message = 'Please test the connection before saving';
            form.setErrors({ testing: message });
            notifyError({ description: message });
            return;
        }

        // Validate vSphere credentials if editing
        if (isEditMode && vsphereCredentials.showEdit) {
            const username = vsphereCredentials.credentials?.username?.trim();
            const password = vsphereCredentials.credentials?.password?.trim();

            if ((username && !password) || (!username && password)) {
                notifyError({ description: 'Please provide both vSphere username and password' });
                return;
            }
        }

        try {
            // First, create any new tags before saving the cloud account
            if (newTags.length > 0) {
                try {
                    await createOrEnsureTags({ orgId: userOrg, tags: newTags }).unwrap();
                    notifySuccess({ description: 'New tags added successfully' });
                } catch (tagError) {
                    console.error('Error creating new tags:', tagError);
                    notifyError({
                        description: 'Failed to create new tags. Please try again.',
                    });
                    return;
                }
            }

            const additionalData = {
                ...(vsphereCredentials.showEdit && {
                    vsphereCredentials: vsphereCredentials.credentials,
                }),
                ...(isEditMode && {
                    originalData: cloudAccountData,
                    token: cloudAccountData?.appliance?.token || cloudAccountData?.token,
                }),
                ...(!isEditMode && {
                    token: tokenGeneration.actualToken || tokenGeneration.generatedToken, // Use actualToken for API, fallback to generatedToken
                    applianceId: tokenGeneration.applianceId,
                }),
            };

            const payload = buildCloudAccountPayload(
                selectedCloudType,
                form.formData,
                userOrg,
                tags,
                isEditMode,
                additionalData,
            );

            let result;
            if (isEditMode) {
                result = await updateCloudAccount({ cloudAccountId, data: payload }).unwrap();
            } else {
                result = await createCloudAccount(payload).unwrap();
            }

            notifySuccess({
                description: isEditMode ? 'Cloud account updated successfully' : 'Cloud account created successfully',
            });

            if (onSuccess) {
                onSuccess(result, setupAppliance);
            }

            handleClose();
        } catch (error) {
            notifyError({
                description:
                    error.message ||
                    error.data?.message ||
                    `Failed to ${isEditMode ? 'update' : 'create'} cloud account`,
            });
        }
    }, [
        form,
        isEditMode,
        selectedCloudType,
        testConnection,
        vsphereCredentials,
        tokenGeneration,
        tags,
        newTags,
        userOrg,
        cloudAccountId,
        cloudAccountData,
        createCloudAccount,
        updateCloudAccount,
        createOrEnsureTags,
        onSuccess,
        handleClose,
        setupAppliance,
        notifySuccess,
        notifyError,
        validateFormFields,
    ]);

    // Get the cloud-specific component
    const CloudComponent = useMemo(
        () => (selectedCloudType ? getCloudComponent(selectedCloudType) : null),
        [selectedCloudType],
    );

    return (
        <Modal
            title={isEditMode ? 'Migration Appliance Details' : 'Add Migration Appliance'}
            open={open}
            onCancel={handleClose}
            width={700}
            footer={[
                <Button key="cancel" onClick={handleClose}>
                    Cancel
                </Button>,
                <Button key="submit" type="primary" onClick={handleSubmit} loading={loading}>
                    {isEditMode ? 'Update' : 'Add Migration Appliance'}
                </Button>,
            ]}
            destroyOnHidden
        >
            {!isEditMode && (
                <Alert
                    title="Provide your target cloud account details. Next, you will need to set up and launch a Migration Appliance into your target cloud environment."
                    type="info"
                    showIcon
                    style={{ marginBottom: 24 }}
                />
            )}

            <Form layout="vertical">
                <Form.Item
                    label={
                        <span>
                            Cloud Type{' '}
                            <Tooltip title="Select your target cloud type from the dropdown (this is the destination environment for the sources being migrated).">
                                <QuestionCircleOutlined />
                            </Tooltip>
                        </span>
                    }
                    required
                >
                    <CloudTypeSelect
                        isEditMode={isEditMode}
                        selectedCloudType={selectedCloudType}
                        handleCloudTypeChange={handleCloudTypeChange}
                    />
                </Form.Item>

                {CloudComponent && selectedCloudType && (
                    <CloudComponent
                        cloudType={selectedCloudType}
                        isEditMode={isEditMode}
                        formData={form.formData}
                        errors={form.errors}
                        onChange={form.handleFieldChange}
                        // Test connection
                        onTestConnection={handleTestConnection}
                        testing={testConnection.testing}
                        tested={testConnection.tested}
                        // Template download
                        onDownloadTemplate={handleDownloadTemplate}
                        // External ID (AWS)
                        externalId={externalId}
                        onCopyExternalId={handleCopyExternalId}
                        // Azure SPN
                        azureSPNData={azureSPNData}
                        onReuseExistingSPN={handleReuseExistingSPN}
                        // vSphere credentials
                        canEditVSphereCredentials={vsphereCredentials.canEdit}
                        showEditVSphereCredentials={vsphereCredentials.showEdit}
                        onToggleVSphereEdit={vsphereCredentials.toggleEdit}
                        vsphereCredentials={vsphereCredentials.credentials}
                        onVSphereCredentialChange={vsphereCredentials.updateCredentials}
                        // Tags
                        tags={tags}
                        onTagsChange={handleTagsChange}
                        userOrg={userOrg}
                        // Setup appliance
                        setupAppliance={setupAppliance}
                        onSetupApplianceChange={setSetupAppliance}
                        // Token generation (for downloadable cloud types)
                        onGenerateToken={handleGenerateToken}
                        generatingToken={tokenGeneration.generating}
                        generatedToken={tokenGeneration.generatedToken}
                        onCopyToken={handleCopyToken}
                        // Notifications
                        notifySuccess={notifySuccess}
                        notifyError={notifyError}
                    />
                )}
            </Form>
        </Modal>
    );
};

export default CloudAccountModal;
